import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import { User } from '../../../types/user';
import { USER_MASTER_FEATURE_KEY, UserMasterCollectionState } from '../user-master-collection.state';
import { isPaceOnlineUserFrontId } from '../user-master.utils';

export const selectUserMasterFeature = createFeatureSelector<UserMasterCollectionState>(USER_MASTER_FEATURE_KEY);

/** The selected user in its plain shape, or `undefined` when none has been resolved yet. */
export const selectSelectedUser: MemoizedSelector<object, User | undefined> = createSelector(
    selectUserMasterFeature,
    (state) => state.selectedUser,
);

/**
 * Whether the selected user is a PACE Online user, i.e. one whose front id marks them as working
 * online. An unresolved user reads as online, which is the safe default before the selection is
 * known.
 */
export const selectIsPaceOnlineUser: MemoizedSelector<object, boolean> = createSelector(
    selectUserMasterFeature,
    (state) => isPaceOnlineUserFrontId(state.selectedUser?.frontId),
);

/** The selected user's role, empty string when unknown. */
export const selectUserRole: MemoizedSelector<object, string> = createSelector(
    selectUserMasterFeature,
    (state) => state.selectedUser?.userRole ?? '',
);

/**
 * The selected user's country, empty string when unknown.
 *
 * A timesheet address carries this rather than the address's own country, which is why the legacy
 * `toJsonTimesheetModel` bridge needs it to resolve a stored address id back into the legacy JSON
 * address shape.
 */
export const selectUserCountry: MemoizedSelector<object, string> = createSelector(
    selectUserMasterFeature,
    (state) => state.selectedUser?.country ?? '',
);

/** Raw load status of the slice. */
export const selectUserMasterStatus: MemoizedSelector<object, LoadStatus> = createSelector(
    selectUserMasterFeature,
    (state) => state.status,
);

/** Whether a user has been resolved (loaded/restored) into the slice. */
export const selectUserMasterLoaded: MemoizedSelector<object, boolean> = createSelector(
    selectUserMasterFeature,
    (state) => state.status === LoadStatus.Loaded,
);
